import { useEffect, useState, useCallback } from 'react';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Markdown } from 'tiptap-markdown';
import api from '../../services/api';
import CiteMark from './CiteMark';

interface StructuredBodyEditorProps {
  pageId: number;
  versionId: number;
  /** Persist the edited body (cite-tagged markdown). Resolves when saved. */
  onSave: (markdown: string) => Promise<void>;
  onCancel: () => void;
}

// tiptap-markdown augments editor.storage with a `markdown` API that isn't in
// the base types.
function getMarkdown(editor: Editor): string {
  const storage = editor.storage as unknown as {
    markdown?: { getMarkdown: () => string };
  };
  return storage.markdown?.getMarkdown() ?? '';
}

function ToolbarButton({
  active,
  onClick,
  title,
  children,
}: {
  active?: boolean;
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      onMouseDown={(e) => e.preventDefault()} // keep editor selection
      onClick={onClick}
      className={`rounded px-2 py-1 text-xs font-medium ${
        active
          ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300'
          : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
      }`}
    >
      {children}
    </button>
  );
}

export default function StructuredBodyEditor({
  pageId,
  versionId,
  onSave,
  onCancel,
}: StructuredBodyEditorProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      // html:true so inline <cite> survives markdown parse; the CiteMark then
      // applies via parseHTML.
      Markdown.configure({ html: true, breaks: false, transformPastedText: true }),
      CiteMark,
    ],
    content: '',
    editorProps: {
      attributes: {
        class:
          'structured-body-editor focus:outline-none min-h-[16rem] text-sm leading-relaxed text-gray-900 dark:text-gray-100',
      },
    },
  });

  useEffect(() => {
    let cancelled = false;
    if (!editor) return;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.getEditableBody(pageId, versionId);
        if (!cancelled) editor.commands.setContent(res.body || '');
      } catch (err) {
        if (!cancelled)
          setError(err instanceof Error ? err.message : 'Failed to load body');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pageId, versionId, editor]);

  const handleSave = useCallback(async () => {
    if (!editor) return;
    setSaving(true);
    setError(null);
    try {
      await onSave(getMarkdown(editor));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }, [editor, onSave]);

  return (
    <div className="rounded-md border border-gray-200 dark:border-gray-700">
      {/* Citation chips: unbreakable, visually distinct. */}
      <style>{`
        .structured-body-editor .cite-chip {
          background: rgba(59,130,246,0.12);
          border-bottom: 1.5px solid rgba(59,130,246,0.55);
          border-radius: 3px;
          padding: 0 2px;
          font-style: normal;
        }
        .structured-body-editor h2 { font-size: 1.15rem; font-weight: 700; margin: 0.75rem 0 0.35rem; }
        .structured-body-editor h3 { font-size: 1rem; font-weight: 600; margin: 0.6rem 0 0.3rem; }
        .structured-body-editor p { margin: 0.4rem 0; }
        .structured-body-editor ul { list-style: disc; padding-left: 1.4rem; margin: 0.4rem 0; }
        .structured-body-editor ol { list-style: decimal; padding-left: 1.4rem; margin: 0.4rem 0; }
      `}</style>

      <div className="flex flex-wrap items-center gap-1 border-b border-gray-200 bg-gray-50 px-2 py-1.5 dark:border-gray-700 dark:bg-gray-800">
        <ToolbarButton
          title="Heading 2"
          active={editor?.isActive('heading', { level: 2 })}
          onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
        >
          H2
        </ToolbarButton>
        <ToolbarButton
          title="Heading 3"
          active={editor?.isActive('heading', { level: 3 })}
          onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()}
        >
          H3
        </ToolbarButton>
        <span className="mx-1 h-4 w-px bg-gray-300 dark:bg-gray-600" />
        <ToolbarButton
          title="Bold"
          active={editor?.isActive('bold')}
          onClick={() => editor?.chain().focus().toggleBold().run()}
        >
          <strong>B</strong>
        </ToolbarButton>
        <ToolbarButton
          title="Italic"
          active={editor?.isActive('italic')}
          onClick={() => editor?.chain().focus().toggleItalic().run()}
        >
          <em>I</em>
        </ToolbarButton>
        <span className="mx-1 h-4 w-px bg-gray-300 dark:bg-gray-600" />
        <ToolbarButton
          title="Bullet list"
          active={editor?.isActive('bulletList')}
          onClick={() => editor?.chain().focus().toggleBulletList().run()}
        >
          • List
        </ToolbarButton>
        <ToolbarButton
          title="Numbered list"
          active={editor?.isActive('orderedList')}
          onClick={() => editor?.chain().focus().toggleOrderedList().run()}
        >
          1. List
        </ToolbarButton>

        <div className="ml-auto flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
            Citations are locked chips
          </span>
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="rounded px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 disabled:opacity-50 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || loading}
            className="rounded bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save body'}
          </button>
        </div>
      </div>

      {error && (
        <div className="border-b border-red-200 bg-red-50 px-3 py-1.5 text-xs text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300">
          {error}
        </div>
      )}

      <div className="px-3 py-2">
        {loading ? (
          <div className="py-8 text-center text-sm text-gray-400">Loading body…</div>
        ) : (
          <EditorContent editor={editor} />
        )}
      </div>
    </div>
  );
}
